const _ = require("lodash");
const Big = require("big.js");
const ShopData = require("./data/dbShop.json");
const StoreBundleUtil = require("./StoreBundleUtil");
const moment = require("moment");

const REFERRER_CREDIT_GIVE10GET10 = 10;

const possibleGradeSets = StoreBundleUtil.getBundleGradeList().map(g => {
	return StoreBundleUtil.makeGradeSet(g).map(i => i.sku);
});

const getItemType = function(item) {
	return ShopData[item.sku]?.type;
};

// Internal helper. Returns an array of sku lists, representing grade sets that
// are in this cart. Bundled items are excluded.
const findGradeSets = function(items) {
	const skuSet = new Set();
	items.forEach(i => {
		if (i.options && i.options.bundleID) return;
		skuSet.add(i.sku);
	});
	return possibleGradeSets.filter(skuList =>
		skuList.every(sku => skuSet.has(sku))
	);
};

// Internal helper. Returns true or false. Not called in cases where more
// context is needed than one item.
const appliesToItem = function(coupon, item) {
	const isBundle = item.options && item.options.bundleID;
	if (coupon.isGiftCertificate || coupon.type === "all") {
		return true;
	} else if (coupon.type === "itemType") {
		return coupon.typeInfo === getItemType(item);
	} else if (
		coupon.type === "special" &&
		coupon.typeInfo === "non-bundle-subscription"
	) {
		return item.sku === "beast-yearly" && !isBundle;
	} else if (
		coupon.type === "special" &&
		coupon.typeInfo === "monthly-or-yearly"
	) {
		//Allow non-bundle yearly subscriptions or monthly subscriptions
		return (
			(item.sku === "beast-yearly" && !isBundle) || item.sku === "beast-monthly"
		);
	} else if (
		coupon.type === "special" &&
		coupon.typeInfo === "new-subscription-referral"
	) {
		return item.sku === "beast-yearly" || item.sku === "beast-monthly";
	} else if (
		coupon.type === "special" &&
		coupon.typeInfo === "new-sub-extension-referral"
	) {
		return item.sku === "beast-yearly";
	}
};

// Internal helper. Returns an array of trues and falses with same length as
// the passed items array.
const getApplicableItems = function(coupon, items) {
	if (coupon.type === "special" && coupon.typeInfo === "non-bundle-grade-set") {
		const skuSet = new Set();
		const gradeSetSkuList = findGradeSets(items);
		gradeSetSkuList.forEach(skuList => skuList.forEach(sku => skuSet.add(sku)));
		return items.map(i => skuSet.has(i.sku));
	} else {
		return items.map(i => appliesToItem(coupon, i));
	}
};

// Internal helper. Sorts an array of items in the order that a coupon should
// be applied to them.
const getCouponOrder = function(items) {
	return _.sortBy(items, i => {
		// Taking advantage of the fact that "book" comes before "subscription"
		// alphabetically here. (We want to apply to books first.)
		const typePart = getItemType(i);
		return typePart + i.sku;
	});
};

// Verifies the returned coupon has balance/uses left. Backend should mark
// active: false or not return this coupon when these happen in theory though.
const isUsableCoupon = function(coupon) {
	if (coupon.usageLimit > 0 && coupon.timesUsed >= coupon.usageLimit)
		return false;
	const hasAmount = Big(coupon.amount).gt(0);
	const hasBalance = Big(coupon.balance).gt(0);
	const hasPercentOff = Big(coupon.percentOff).gt(0);
	const isSubscriptionExtension =
		coupon.typeInfo === "new-sub-extension-referral";

	return (
		hasPercentOff ||
		(coupon.public && hasAmount) ||
		(hasAmount && hasBalance) ||
		isSubscriptionExtension
	);
};

// Checks for other special coupon cases, i.e coupon for first-time customers
// only
const isInapplicableCoupon = function(coupon, isExistingAccount) {
	if (coupon.typeInfo === "new-subscription-referral" && isExistingAccount) {
		return true;
	}
	if (coupon.typeInfo === "new-sub-extension-referral" && isExistingAccount) {
		return true;
	}
	return false;
};

// Returns the amount of credit for referrer if any redemption of the coupon should provide credit to the referrerID.
const getReferrerCredit = function(coupon) {
	if (coupon.typeInfo !== "new-subscription-referral") return null;
	if (coupon.referrerID) {
		return {
			referrerID: coupon.referrerID,
			amount: REFERRER_CREDIT_GIVE10GET10,
		};
	}
};

/**
 * Check if coupon is valid for subscription renewals
 * For now, the test is really simple. If it's percent off,
 * doesn't exist, is inactive, or is book only, then the coupon isn't
 * available for renewals. Otherwise it is. This will likely
 * become more complex in the future for different coupon types,
 * for example, a coupon only good for books.
 * For future reference, one way to deal with this would be to make a fake
 * items array with one monthly subscription and check that appliesToCart
 * (in this file) returns ok: true for it, then do the same for a single
 * yearly subscription.
 *
 * @param {Object} coupon
 */
const isValidForSubscriptionRenewal = function(coupon) {
	if (!coupon || !coupon.active || coupon.percentOff > 0) {
		return false;
	}

	if (coupon.expiresAt && coupon.expiresAt < moment.utc()) {
		return false;
	}

	// Book only and referall coupons not permitted
	if (
		["book", "non-bundle-grade-set", "new-subscription-referral"].indexOf(
			coupon.typeInfo
		) >= 0
	) {
		return false;
	}

	return true;
};
/**
 * This checks whether or not a coupon has the right type for extension
 * which is currently only "new-sub-extension-referral"
 * and the basic things, like if it's active or if it's expired yet
 * or if its reached its usage limit.
 * @param {Object} coupon
 */
const isValidForSubscriptionExtension = function(coupon) {
	if (!coupon || !coupon.active || coupon.percentOff > 0) {
		return false;
	}

	if (coupon.expiresAt && coupon.expiresAt < moment.utc()) {
		return false;
	}

	if (!coupon.usageLimit) {
		return false;
	}

	return coupon.typeInfo === "new-sub-extension-referral";
};

// Returns {ok: true} or {ok: false, error, moreInfo?} object.
// isExistingAccount is true if the parent is not a first-time online customer
const appliesToCart = function(coupon, items, isExistingAccount) {
	if (!isUsableCoupon(coupon)) {
		return {ok: false, error: "E_UNUSABLE_COUPON"};
	} else if (isInapplicableCoupon(coupon, isExistingAccount)) {
		// Return a different error code if we have more cases
		return {ok: false, error: "E_EXISTING_ACCOUNT"};
	} else if (coupon.isGiftCertificate || coupon.type === "all") {
		return {ok: true};
	} else if (coupon.type === "itemType") {
		const ok = items.some(i => appliesToItem(coupon, i));
		return ok
			? {ok: true}
			: {ok: false, error: "E_NO_MATCHING_ITEMS", moreInfo: coupon.typeInfo};
	} else if (
		coupon.type === "special" &&
		_.includes(
			[
				"non-bundle-subscription",
				"monthly-or-yearly",
				"new-subscription-referral",
				"new-sub-extension-referral",
			],
			coupon.typeInfo
		)
	) {
		const ok = items.some(i => appliesToItem(coupon, i));
		const errorTable = {
			"non-bundle-subscription": "E_NO_UNBUNDLED_SUBSCRIPTION",
			"monthly-or-yearly": "E_NO_MONTHLY_OR_YEARLY",
			"new-subscription-referral": "E_NO_SUBSCRIPTIONS",
			"new-sub-extension-referral": "E_NO_YEARLY_SUBSCRIPTIONS",
		};
		return ok ? {ok: true} : {ok: false, error: errorTable[coupon.typeInfo]};
	} else if (
		coupon.type === "special" &&
		coupon.typeInfo === "non-bundle-grade-set"
	) {
		return findGradeSets(items).length
			? {ok: true}
			: {ok: false, error: "E_NO_GRADE_SETS"};
	}
};

// Returns {prices, remaining}. Both the passed and returned prices array
// consists of Big objects. The returned remaining is the amount left on the
// coupon.
// @param {object} Coupon object based on store_coupons table
// @param {Array} items as passed in via front end or back end
// @param {Big[]} prices A one to one relationship with items. Total cost. So 3 guidebooks would be 45.
// @return {Object} prices, remaining. Prices are the prices after coupon applied.
const getAppliedPrices = function(coupon, items, prices) {
	let remaining = Big(coupon.public ? coupon.amount : coupon.balance);
	let applied = Big(0);
	const amount = Big(coupon.amount ? coupon.amount : 0);
	const percentOff = amount.gt(0) ? 0 : coupon.percentOff;

	// Gift certificates do not apply to item prices and
	// are calculated after subtotal, shipping, and tax.
	if (coupon.isGiftCertificate) return {prices, remaining, applied};

	// If there is no balance and it's not a percent off coupon, then can't be applied
	if (remaining.lte(0) && !percentOff) return {prices, remaining, applied};

	// For coupons that can only be used a certain number of times
	if (coupon.usageLimit > 0 && coupon.timesUsed >= coupon.usageLimit)
		return {prices, remaining, applied};

	const applicable = getApplicableItems(coupon, items);
	if (!applicable.some(x => x)) return {prices, remaining, applied};

	const indexedItems = items.map((item, j) => _.extend({}, item, {index: j}));
	const sortedIndices = getCouponOrder(indexedItems).map(item => item.index);

	const newPrices = prices.slice(0);

	if (percentOff) {
		const coeff = Big(100)
			.minus(percentOff)
			.div(100);
		const percentage = Big(percentOff).div(100);
		sortedIndices.forEach(j => {
			const orig = newPrices[j];
			newPrices[j] = applicable[j] ? orig.times(coeff) : orig;
			applied = applied.add(applicable[j] ? orig.times(percentage) : 0);
		});
	}

	if (remaining.gt(0)) {
		sortedIndices.forEach(j => {
			const orig = newPrices[j];
			if (!applicable[j] || remaining.lte(0)) {
				// Do nothing
			} else if (remaining.lte(orig)) {
				newPrices[j] = orig.minus(remaining);
				applied = applied.add(remaining);
				remaining = Big(0);
			} else {
				remaining = remaining.minus(orig);
				applied = applied.add(orig);
				newPrices[j] = Big(0);
			}
		});
	}
	return {prices: newPrices, remaining, applied};
};

module.exports = {
	appliesToCart,
	getAppliedPrices,
	isValidForSubscriptionRenewal,
	isValidForSubscriptionExtension,
	getReferrerCredit,
};
